import { useModal } from "hooks/useModal"

import { memo, useCallback, useState } from "react"
import styled from "styled-components"
import { AppState, useAppDispatch, useAppSelector } from "reducers/store"
import Input from "components/EvtKit/input/Input"
import Button from "components/EvtKit/button/Button"
import ApiAccounts from "api/ApiAccounts"
import { useMutation, useQueryClient } from "react-query"
import clsx from "clsx"
import Heading from "components/Typography/Heading"
import { clearAccount, clearSystemEvents } from "reducers/accounts/accounts-actions"
import { ACCOUNT_SYSTEM_EVENT, ACCOUNTS_FETCH } from "reducers/accounts/actions-types"
import { validateNumber } from "utils/auth/authHelper"
import { showToast } from "reducers/toast/toast-actions"
import useHandleApiError from "hooks/useHandleApiErrors"
import { useQueryState } from "context/queryState"
import BodyText from "components/Typography/BodyText"
import ModalV2 from "components/EvtKit/modal/ModalV2"

const FreeTrialButtonContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 28px 0px 24px;
  gap: 32px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const FreeTrialInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const ContentFreeTrialUpdated = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: -7px;
`

const ContainerFreeTrialList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Ul = styled.ul`
  list-style: disc;
  color: ${(props) => props.theme.palette.mediumGray};
  margin: 0;
  background-color: ${(props) => props.theme.palette.gray};
  border-radius: 8px;
  width: 100%;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Li = styled.li`
  margin-left: 16px;
`

const HistoryWithoutEvents = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.palette.gray};
  padding: 8px 16px;
  border-radius: 8px;
`

const AddFreeTrialModal = () => {
  const queryClient = useQueryClient()

  const appDispatch = useAppDispatch()

  const { isOpen, type, closeModal } = useModal()

  const limitInputValue = 60

  const [inputValue, setInputValue] = useState("")
  const [inputError, setInputError] = useState("")

  const handleCleanModalState = useCallback(() => {
    appDispatch(clearAccount())
    appDispatch(clearSystemEvents())
    closeModal()
    setInputValue("")
  }, [closeModal])

  const { account } = useAppSelector((x: AppState) => x.accounts)

  useQueryState(
    [ACCOUNT_SYSTEM_EVENT, isOpen, account],
    async () => {
      if (!isOpen || !account?.id) return

      return new ApiAccounts().getSystemEventByAccountId(account.id)
    },
    ACCOUNT_SYSTEM_EVENT,
    {
      refetchOnMount: "always"
    }
  )

  const { eventsFreeTrial } = useAppSelector((x: AppState) => x.accounts.systemEvents)

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setInputValue(value)

    if ((!validateNumber(value) && value !== "") || value === "0") {
      setInputError("Adicione apenas valores inteiros e positivos.")
    } else if (parseInt(value) > limitInputValue) {
      setInputError(`Adicione um valor menor que ${limitInputValue} dias.`)
    } else {
      setInputError("")
    }
  }, [])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const updateFreeTrial = useMutation({
    mutationFn: async (data: App.AccountUpdateFreeTrialPayload) => {
      return new ApiAccounts().updateFreeTrial(data)
    },
    onSuccess: () => {
      handleCleanModalState()
      queryClient.invalidateQueries(ACCOUNTS_FETCH)
      queryClient.invalidateQueries(ACCOUNT_SYSTEM_EVENT)
      appDispatch(showToast("Tempo de Free Trial adicionado com sucesso!", "success"))
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(
        error,
        "Não foi possível adicionar o tempo de free trial, tente novamente mais tarde."
      )
    }
  })

  const submitFreeTrial = useCallback(() => {
    updateFreeTrial.mutate({
      accountId: account?.id,
      prodPrefixUrl: account.product,
      moreDays: Number(inputValue)
    })
  }, [inputValue, account])

  const disabledButton =
    inputValue === "" || parseInt(inputValue) === 0 || parseInt(inputValue) > limitInputValue || inputError !== ""

  return (
    <ModalV2 openModal={isOpen && type === "addFreeTrial"} closeModal={handleCleanModalState} size="1-lg" height="md">
      <Container>
        <Heading variant="h4" color="primary-dark">
          Adicionar tempo de Free Trial
        </Heading>
        <Content>
          <FreeTrialInputContainer>
            <Heading variant="h6">
              Esta função permite adicionar tempo de Free Trial para o cliente a partir da data de alteração.
            </Heading>
            <Input
              name="freeTrialDays"
              id="freeTrialDays"
              type="number"
              label="Dias de Free Trial"
              onChange={handleInputChange}
              value={inputValue}
              error={inputError}
              safeBottom
            />
          </FreeTrialInputContainer>
          <ContentFreeTrialUpdated>
            <Heading variant="h6">Histórico</Heading>
            {eventsFreeTrial.length === 0 && (
              <HistoryWithoutEvents>
                <BodyText size="medium" color="gray-dark" weight="regular">
                  Você ainda não adicionou nenhum tempo extra de Free Trial para este usuário.
                </BodyText>
              </HistoryWithoutEvents>
            )}
            {eventsFreeTrial.length > 0 && (
              <ContainerFreeTrialList>
                {eventsFreeTrial.map((event, index) => (
                  <Ul key={index}>
                    <Li>
                      <BodyText size="medium" color="gray-dark">
                        {event.metadata.freeTrialAdded} dias de Free Trial adicionados
                      </BodyText>
                      <BodyText size="small" color="gray-medium">
                        por <b>{event.metadata.freeTrialAddedBy}</b> em <b>{event.createdAt}</b>
                      </BodyText>
                    </Li>
                  </Ul>
                ))}
              </ContainerFreeTrialList>
            )}
          </ContentFreeTrialUpdated>
          <FreeTrialButtonContainer>
            <Button
              type="button"
              onClick={handleCleanModalState}
              label="Cancelar"
              className={clsx("secondary fill-width")}
            />
            <Button
              type="button"
              label="Adicionar"
              className={clsx("background-gradient fill-width", { disabled: disabledButton })}
              disabled={disabledButton || updateFreeTrial.isLoading}
              loading={updateFreeTrial.isLoading}
              onClick={submitFreeTrial}
            />
          </FreeTrialButtonContainer>
        </Content>
      </Container>
    </ModalV2>
  )
}

export default memo(AddFreeTrialModal)
